import { useCallback, useEffect, useRef, useState } from 'react';
import { callPlugin } from '../api/client';
import logo from '../assets/logo.png';
import { Button, Input } from '../ui';
import { QrCode } from '../ui/QrCode';

/**
 * First-run wizard, rebuilt for React.
 *
 * A port of the old KeystoneOnboarding.js, which was bound to `rl.*` and did not
 * survive the front-end rebuild. The SERVER hooks it drives are unchanged: this
 * only replaces the surface.
 *
 * It exists because enrollment is the one thing that cannot be left to a
 * settings screen nobody visits -- 2FA and a signature are both things staff are
 * expected to have, and asking once at first login is the only reliable moment.
 */

type Step = 'welcome' | 'twofactor' | 'signature' | 'done';

interface TwoFactorInfo {
	IsSet?: boolean;
	Enable?: boolean;
}

interface TwoFactorSecret {
	User?: string;
	Secret?: string;
	QRCode?: string;
	BackupCodes?: string;
}

export function OnboardingWizard({ onFinish }: { onFinish: () => void }) {
	const [steps, setSteps] = useState<Step[] | null>(null);
	const [index, setIndex] = useState(0);

	// Decide which steps are needed before showing anything, so the wizard never
	// flashes up for someone who has already done both.
	useEffect(() => {
		let cancelled = false;

		const decide = async () => {
			let needsTwoFactor = false;
			try {
				const info = await callPlugin<TwoFactorInfo>('GetTwoFactorInfo');
				needsTwoFactor = !(info?.IsSet && info?.Enable);
			} catch {
				// The 2FA plugin is not installed or errored. Skip that step rather
				// than blocking first login on it.
			}

			let needsSignature = false;
			try {
				const signature = await callPlugin<{ name?: string }>('KeystoneSignatureGet');
				needsSignature = !signature?.name?.trim();
			} catch {
				// Same reasoning.
			}

			if (cancelled) {
				return;
			}

			if (!needsTwoFactor && !needsSignature) {
				onFinish();
				return;
			}

			const list: Step[] = ['welcome'];
			needsTwoFactor && list.push('twofactor');
			needsSignature && list.push('signature');
			list.push('done');
			setSteps(list);
		};

		void decide();
		return () => {
			cancelled = true;
		};
	}, [onFinish]);

	const next = useCallback(() => setIndex(value => value + 1), []);

	if (!steps) {
		return null;
	}

	const step = steps[index];
	// "welcome" and "done" are not work, so they are not numbered.
	const numbered: Step[] = steps.filter(item => 'welcome' !== item && 'done' !== item);
	const position = numbered.indexOf(step);

	return (
		<div className="wizard" role="dialog" aria-modal="true" aria-label="Set up Keystone Mail">
			<div className="wizard__card">
				<img className="wizard__logo" src={logo} alt="Keystone Management" />

				{position >= 0 && (
					<p className="wizard__step">
						Step {position + 1} of {numbered.length}
					</p>
				)}

				{'welcome' === step && <Welcome onNext={next} />}
				{'twofactor' === step && <TwoFactor onNext={next} />}
				{'signature' === step && <Signature onNext={next} />}
				{'done' === step && <Done onFinish={onFinish} />}
			</div>
		</div>
	);
}

/* ---------------------------------------------------------------- welcome */

function Welcome({ onNext }: { onNext: () => void }) {
	return (
		<>
			<h1 className="wizard__title">Welcome to Keystone Mail</h1>
			<p className="wizard__body">
				Two quick things before you get to your inbox. It takes about a minute.
			</p>
			<Button onClick={onNext}>Get started</Button>
		</>
	);
}

/* -------------------------------------------------------------- two-factor */

function TwoFactor({ onNext }: { onNext: () => void }) {
	const [secret, setSecret] = useState<TwoFactorSecret | null>(null);
	const [code, setCode] = useState('');
	const [error, setError] = useState('');
	const [failed, setFailed] = useState(false);
	const [busy, setBusy] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		let cancelled = false;
		callPlugin<TwoFactorSecret>('CreateTwoFactorSecret')
			.then(result => {
				if (cancelled) {
					return;
				}
				if (!result?.Secret) {
					setFailed(true);
					return;
				}
				setSecret(result);
				requestAnimationFrame(() => inputRef.current?.focus());
			})
			.catch(() => !cancelled && setFailed(true));
		return () => {
			cancelled = true;
		};
	}, []);

	async function verify() {
		if (6 > code.trim().length) {
			setError('Enter the six-digit code from your app.');
			return;
		}

		setBusy(true);
		setError('');

		try {
			const ok = await callPlugin<boolean>('VerifyTwoFactorCode', { Code: code.trim() });
			if (!ok) {
				setError('That code was not right. Codes change every 30 seconds — try the current one.');
				return;
			}
			// Verify and enable are separate calls: the secret is only armed once a
			// code from it has actually been proven to work, so nobody can lock
			// themselves out with a misconfigured authenticator.
			//
			// The parameter is `Enable`. It was once sent as `EnableTwoFactor`, which
			// the server ignored -- 2FA was saved as off, and the wizard came back on
			// every login with a brand-new secret.
			await callPlugin<unknown>('EnableTwoFactor', { Enable: '1' });
			onNext();
		} catch {
			setError('Could not verify the code. Please try again.');
		} finally {
			setBusy(false);
		}
	}

	if (failed) {
		return (
			<>
				<h1 className="wizard__title">Secure your account</h1>
				<p className="wizard__body ks-error">
					Could not set up two-factor authentication right now. You’ll be asked again next time
					you sign in.
				</p>
				<Button onClick={onNext}>Continue</Button>
			</>
		);
	}

	return (
		<>
			<h1 className="wizard__title">Secure your account</h1>
			<p className="wizard__body">
				Add Keystone Mail to an authenticator app — Google Authenticator, Microsoft
				Authenticator, 1Password or Authy all work. Then enter the six-digit code it shows.
			</p>

			{!secret && <p className="wizard__body">Preparing your security key…</p>}

			{secret && (
				<>
					{/*
					 * On a phone the QR is useless -- it is on the same screen as the
					 * camera that would have to read it. An otpauth:// link opens the
					 * installed authenticator app directly with the account filled in.
					 */}
					{secret.Secret && (
						<a className="wizard__applink" href={otpauthLink(secret)}>
							Open in authenticator app
						</a>
					)}

					{secret.QRCode && (
						<div className="wizard__qr">
							<QrCode art={secret.QRCode} label="QR code for your authenticator app" />
						</div>
					)}

					<p className="wizard__hint">
						Can’t scan? Enter this key manually:
						<br />
						<code className="wizard__secret">{formatSecret(secret.Secret)}</code>
					</p>

					{secret.BackupCodes && (
						<>
							<p className="wizard__body">
								Save these backup codes somewhere safe. Each one gets you in once if you lose
								your phone.
							</p>
							<div className="wizard__codes">
								{secret.BackupCodes.split(/\s+/)
									.filter(Boolean)
									.map(backup => (
										<div key={backup}>{backup}</div>
									))}
							</div>
						</>
					)}

					<Input
						id="ks-totp"
						ref={inputRef}
						label="Six-digit code"
						value={code}
						onChange={event => setCode(event.target.value)}
						onKeyDown={event => {
							if ('Enter' === event.key) {
								event.preventDefault();
								void verify();
							}
						}}
						inputMode="numeric"
						autoComplete="one-time-code"
						maxLength={6}
						placeholder="000000"
						size="lg"
					/>

					{error && <p className="ks-error">{error}</p>}

					<Button onClick={() => void verify()} loading={busy} size="lg" block>
						Verify and continue
					</Button>
				</>
			)}

			{/*
			 * Never a dead end. Someone without their phone to hand must still be
			 * able to reach their mail; enrollment is a nudge here, not the security
			 * boundary (that is the server-side code check once 2FA is on). Skipping
			 * leaves 2FA off, so the wizard asks again at the next sign-in.
			 */}
			<Button variant="ghost" onClick={onNext} disabled={busy}>
				Set up later
			</Button>
		</>
	);
}

/**
 * The standard key-URI format every authenticator app understands. The issuer
 * is what the app lists the entry under, so it reads "Keystone Mail" rather
 * than a bare address.
 */
function otpauthLink(secret: TwoFactorSecret): string {
	const issuer = 'Keystone Mail';
	const account = secret.User ?? '';
	const label = encodeURIComponent(account ? `${issuer}:${account}` : issuer);
	// encodeURIComponent rather than URLSearchParams: the latter writes spaces as
	// "+", which several authenticator apps show literally ("Keystone+Mail").
	const query = `secret=${encodeURIComponent(secret.Secret ?? '')}&issuer=${encodeURIComponent(issuer)}`;
	return `otpauth://totp/${label}?${query}`;
}

/** Groups the key in fours -- far easier to type by hand without losing your place. */
function formatSecret(value = ''): string {
	return value.replace(/(.{4})(?=.)/g, '$1 ');
}

/* --------------------------------------------------------------- signature */

function Signature({ onNext }: { onNext: () => void }) {
	const [name, setName] = useState('');
	const [role, setRole] = useState('');
	const [phone, setPhone] = useState('');
	const [preview, setPreview] = useState('');
	const [error, setError] = useState('');
	const [busy, setBusy] = useState(false);

	// Debounced so typing does not fire a request per keystroke. The preview is
	// rendered by PHP so there is exactly one copy of the signature markup.
	useEffect(() => {
		const timer = setTimeout(() => {
			callPlugin<{ preview?: string }>('KeystoneSignaturePreview', { name, role, phone })
				.then(result => setPreview(result?.preview ?? ''))
				.catch(() => {});
		}, 400);
		return () => clearTimeout(timer);
	}, [name, role, phone]);

	async function save() {
		if (!name.trim()) {
			setError('Please enter your name.');
			return;
		}

		setBusy(true);
		setError('');

		try {
			const result = await callPlugin<{ applied?: boolean }>('KeystoneSignatureSave', {
				name,
				role,
				phone
			});
			if (!result?.applied) {
				setError('Could not save your signature. You can set it later in Settings.');
				return;
			}
			onNext();
		} catch {
			setError('Could not save your signature. You can set it later in Settings.');
		} finally {
			setBusy(false);
		}
	}

	return (
		<>
			<h1 className="wizard__title">Your email signature</h1>
			<p className="wizard__body">
				This is added to messages you send. The Keystone logo, address and tagline are filled
				in for you.
			</p>

			<Input
				id="ks-name"
				label="Full name"
				value={name}
				onChange={event => setName(event.target.value)}
				placeholder="Jack Sampaon"
			/>
			<Input
				id="ks-role"
				label="Role"
				description="Optional"
				value={role}
				onChange={event => setRole(event.target.value)}
				placeholder="Dispatch Supervisor"
			/>
			<Input
				id="ks-phone"
				label="Phone"
				description="Optional"
				value={phone}
				onChange={event => setPhone(event.target.value)}
				placeholder="(502) 555-0123"
			/>

			{preview && (
				/*
				 * Built entirely by our own PHP from the three fields above -- the
				 * browser never submits markup and this never renders anything that
				 * came from another person.
				 */
				<div className="wizard__preview" dangerouslySetInnerHTML={{ __html: preview }} />
			)}

			{error && <p className="ks-error">{error}</p>}

			<Button onClick={() => void save()} loading={busy} size="lg" block>
				Save and finish
			</Button>
			<Button variant="ghost" onClick={onNext} disabled={busy}>
				Skip for now
			</Button>
		</>
	);
}

/* ------------------------------------------------------------------- done */

function Done({ onFinish }: { onFinish: () => void }) {
	return (
		<>
			<h1 className="wizard__title">You’re all set</h1>
			<p className="wizard__body">
				Everything’s ready. You can change your signature any time from{' '}
				<strong>Settings › Signature</strong>.
			</p>
			<Button onClick={onFinish}>Go to inbox</Button>
		</>
	);
}
